import { NodeViewWrapper } from '@tiptap/react';

import { useResolvedMediaSrc } from '../hooks/useResolvedMediaSrc';

import type { NodeViewProps } from '@tiptap/react';

/**
 * NodeView for the `image` node. The raw href arrives as `data-href`, not
 * `src` (see FigureImage.ts) — this is the only place a real `<img src>`
 * gets set, and only once `useResolvedMediaSrc` has either found it already
 * loadable or signed it via the document's media-access grant.
 */
export function FigureImageView({ node }: NodeViewProps) {
  const attrs = node.attrs as Record<string, string | null>;
  const href = attrs['data-href'] ?? '';
  const alt = attrs['alt'] ?? 'Figure';
  const resolved = useResolvedMediaSrc(href);

  const handle = {
    'data-block-key': attrs['data-block-key'] ?? undefined,
    'data-block-type': attrs['data-block-type'] ?? undefined,
    'data-role': attrs['data-role'] ?? undefined,
  };

  if (resolved.status === 'placeholder') {
    return (
      <NodeViewWrapper>
        <span {...handle} data-part="placeholder">{`[${alt}: image unavailable]`}</span>
      </NodeViewWrapper>
    );
  }

  if (resolved.status === 'loading') {
    return (
      <NodeViewWrapper>
        <span
          {...handle}
          role="status"
          aria-label={`Loading ${alt}`}
          data-part="image-skeleton"
          className="block h-48 w-full max-w-full animate-pulse rounded-md bg-neutral-200"
        />
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper>
      <img {...handle} src={resolved.src} alt={alt} />
    </NodeViewWrapper>
  );
}
